<script lang="ts">
  import * as Form from "$lib/components/ui/form";
  import { Input } from "$lib/components/ui/input";
  import { Checkbox } from "$lib/components/ui/checkbox";
  import * as Select from "$lib/components/ui/select";
  import { zodClient } from "sveltekit-superforms/adapters";
  import { toast } from "svelte-sonner";
  import type { Blog, Bootcamp } from "$lib/datamodel";
  import { community } from "$lib/client/stores/nucamp-community";
  import { cancelPopUp } from "$lib/client/stores/nucamp-popup";
  import { contactCaptureSchema, type ContactCaptureSchema } from "./forms/CaptureContact.svelte";
  import { type SuperValidated, type Infer, superForm } from "sveltekit-superforms";
  import { currentBootcamp } from "$lib/client/stores/nucamp-currentbootcamp";
  import { user } from "$lib/client/stores/nucamp-user";
  import type { Unsubscriber } from "svelte/store";
  import { onDestroy } from "svelte";
  import { followBootcamp } from "$lib/client/services/user";

  export let bootcamps: Bootcamp[];
  export let blog: Blog;
  export let bootcamp: Bootcamp | undefined;
  export let supForm: SuperValidated<Infer<ContactCaptureSchema>>;
  const subscriptions: Unsubscriber[] = [];

  const form = superForm(supForm, {
    validators: zodClient(contactCaptureSchema),
    dataType: "json",
    warnings: {
      duplicateId: false
    },
    applyAction: false,
    invalidateAll: false,
    resetForm: false,
    async onResult({ result }) {
      if (result.type !== "success") {
        console.error("The form is invalid");
        toast.error("The form is invalid", {
          description: "Please fill in all required fields and try again.",
          action: {
            label: "Ok",
            onClick: () => {}
          }
        });
      } else {
        const loadingId = toast.loading("Please wait...", {duration: Number.POSITIVE_INFINITY});
        const validatedUser = result?.data?.extData.user;
        const bootcampDelivery = result?.data?.extData.selBootcampDelivery;
        followBootcamp(validatedUser, bootcampDelivery).then(() => {
          toast.dismiss(loadingId);
          if (blog.paywallasset?.bootcampId === "FREE-EJ") {
            toast.success("Register to Eloquent JavaScript", {
              description: 'Click "Register" to ' + blog.paywallasset.name,
              duration: Number.POSITIVE_INFINITY,
              action: {
                label: "Register",
                onClick: () => window.open(blog.paywallasset?.url, "_blank")
              }
            })
          } else if (result.data?.extData?.selBootcamp) {
            toast.success("Download the bootcamp syllabus", {
              description: 'Click "Download" to get the syllabus',
              duration: Number.POSITIVE_INFINITY,
              action: {
                label: "Download",
                onClick: () => window.open(result.data?.extData.selBootcamp?.brochures.syllabus, "_blank")
              }
            })
          } else {
            console.error("Submission failed", result);
            toast.error("Submission failed", {
              description: JSON.stringify(result),
              action: {
                label: "Ok",
                onClick: () => {}
              }
            })
          }
        })
      }
    },
  });
  
  const { form: formData, enhance } = form;

  let updatedBootcamps: Bootcamp[] = bootcamps;

  $: if (blog.paywallasset?.bootcampId === "FREE-EJ") {
    updatedBootcamps = [...bootcamps, { id: "FREE-EJ", title: "Free Eloquent JavaScript", length: "6 weeks" } as Bootcamp]
  };
  
  // Setting the form defaults based on the user's current community or blog details
  $: if ((blog.communityid || $community?.id) && !$formData.preferredCommunity) {
    if (blog.communityid)
      $formData.preferredCommunity = blog.communityid;
    else if ($community?.id)
      $formData.preferredCommunity = $community.id;
    else
      $formData.preferredCommunity = "ONPA";
  }

  $: selectedBootcamp = $formData.preferredBootcamp
    ? {
        label: $formData.preferredBootcamp.title,
        value: $formData.preferredBootcamp.id,
      }
    : undefined;

  $: if ((blog.paywallasset?.bootcampId && !$formData.preferredBootcamp.id) || $currentBootcamp?.id) {
    if (blog.paywallasset?.bootcampId) {
      const bc = updatedBootcamps.find((b) => b.id === blog?.paywallasset?.bootcampId);
      if (bc) {
        $formData.preferredBootcamp.id = blog.paywallasset.bootcampId;
        $formData.preferredBootcamp.title = `${bc.title} (${bc.length})`;
      }
    } else if ($currentBootcamp?.id && !$formData.preferredBootcamp.id) {
      $formData.preferredBootcamp.id = $currentBootcamp.id;
      $formData.preferredBootcamp.title = `${$currentBootcamp.title} (${$currentBootcamp.length})`;
    }
  }

  subscriptions.push(user.subscribe((usr) => {
    if (usr?.email || usr?.displayName || usr?.phone || usr?.phoneId) {
      if (usr?.email && !$formData.email) {
        $formData.email = usr.email;
      }
      if (usr?.displayName && !$formData.displayName) {
        $formData.displayName = usr.displayName;
      }
      if (usr?.phoneId && !$formData.phone) {
        $formData.phone = usr.phoneId;
      } else if (usr?.phone && !$formData.phone) {
        $formData.phone = usr.phone;
      }
    }
  }));

  onDestroy(() => {
    subscriptions.forEach((s:any) => s());
  });
</script>

<form method="POST" action="/capturecontact/?/capturecontact" use:enhance>
  <div class="flex flex-wrap p-3 lg:p-6 shadow-nucamp-sm bg-slate-100 dark:bg-slate-700 rounded-md">
    <div class="w-full md:w-6/12 pr-0 md:p-2">
      {#if !blog.paywallasset}
        <h4>
          Download the syllabus for 
              {#if ((bootcamp && bootcamp.type !== 'completebootcamp') || !bootcamp)}
                any bootcamp to get a preview of the courses
              {/if}
              {#if (bootcamp && bootcamp.type === 'completebootcamp')}
                {bootcamp.title.toLowerCase()}
              {/if}
        </h4>
      {/if}
      {#if blog.paywallasset}
        <p class="text-4xl font-bold leading-10">Fill this form to <span class="text-slate-500 dark:text-slate-400 font-medium"> {blog.paywallasset.name}</span></p>
      {/if}
      <p class="my-7 text-slate-500 dark:text-slate-400 text-lg">And learn about Nucamp's Vibe Coding Bootcamps and why aspiring developers choose us.</p>

      <!-- <Form.Button class="hidden md:block">Submit</Form.Button> -->
    </div>
    <div class="w-full md:w-6/12 pr-0 md:p-2">
        <Form.Field {form} name="displayName">
          <Form.Control let:attrs>
            <Form.Label>Your First and Last Name*</Form.Label>
            <Input 
              {...attrs} 
              autocomplete="name"
              id="displayName"
              name="displayName"
              placeholder="First and Last Name"
              bind:value={$formData.displayName} 
              on:blur={() => cancelPopUp()}
              on:focus={() => cancelPopUp()}
              />
          </Form.Control>
          <Form.FieldErrors class="!mt-0"/>
        </Form.Field>
        <Form.Field {form} class="mt-2" name="email">
          <Form.Control let:attrs>
            <Form.Label>Your Email*</Form.Label>
            <Input 
              {...attrs} 
              autocomplete="email"
              id="email"
              placeholder="email@domain.com"
              bind:value={$formData.email} 
              on:blur={() => cancelPopUp()}
              on:focus={() => cancelPopUp()}
              />
          </Form.Control>
          <Form.FieldErrors class="!mt-0"/>
        </Form.Field>
        <Form.Field {form} class="mt-2" name="phone">
          <Form.Control let:attrs>
            <Form.Label>Your Phone Number*</Form.Label>
            <Input 
              {...attrs} 
              id="phone"
              autocomplete="tel"
              placeholder=""
              bind:value={$formData.phone}
              on:blur={() => cancelPopUp()}
              on:focus={() => cancelPopUp()}
              />
          </Form.Control>
          <Form.FieldErrors class="!mt-0"/>
        </Form.Field>
        {#if !$formData.preferredCommunity}
          <Form.Field {form} class="mt-2" name="preferredCommunity">
            <Form.Control let:attrs>
              <Form.Label>Your Preferred Community*</Form.Label>
              <Input {...attrs} bind:value={$formData.preferredCommunity} on:blur={() => cancelPopUp()}/>
            </Form.Control>
            <Form.FieldErrors class="!mt-0"/>
          </Form.Field>
        {/if}
        <Form.Field {form} class="mt-2" name="preferredBootcamp">
          <Form.Control let:attrs>
            <Form.Label>Your Preferred Bootcamp*</Form.Label>
            <Select.Root
              selected={selectedBootcamp}
              onSelectedChange={(v) => {
                v && ($formData.preferredBootcamp.id = v.value)
                && ($formData.preferredBootcamp.title = v.label ?? "");
              }}>
              <Select.Trigger {...attrs}>
                <Select.Value placeholder="Select a bootcamp" />
              </Select.Trigger>
              <Select.Content>
                {#each updatedBootcamps as bootcamp}
                  <Select.Item value={bootcamp.id} label={`${bootcamp.title} (${bootcamp.length})`} />
                {/each}
              </Select.Content>
            </Select.Root>
          </Form.Control>
          <Form.FieldErrors class="!mt-0"/>
        </Form.Field>
        <!-- <Form.Field {form} class="mt-2" name="acceptTerms">
            <Form.Control let:attrs>
              <Form.Label>I Agree*</Form.Label>
              <Checkbox {...attrs} bind:checked={$formData.acceptTerms} />
            </Form.Control>
            <Form.FieldErrors class="!mt-0"/>
        </Form.Field> -->
        <!-- <p class="mt-2 text-slate-500 dark:text-slate-400 text-[8px]">*By checking "I Agree", you are opting-in to receive information, including text 
          messages from Nucamp. You also agree to the following <a href="/termsofuse" class="underline underline-offset-2" target="_blank">Terms of use</a>, <a href="/smstermsofuse" class="underline underline-offset-2" target="_blank">SMS Terms of use</a> & <a href="/privacy" class="underline underline-offset-2" target="_blank">Privacy Policy</a>. 
          Reply STOP to stop receiving text messages.</p>  -->
        <Form.Button class="visible mt-4">Submit</Form.Button>
    </div>
  </div>
</form>
